import { useState, useEffect, useCallback } from 'react';
import { Button } from 'src/components/Button';
import { Input } from 'src/components/Input';
import { Label } from 'src/components/Label';
import { IconButton } from 'src/components/IconButton';
import { useNotification } from 'src/contexts/NotificationContext';
import { Option, SelectBox } from 'src/components/SelectBox';
import { CopyIcon } from 'src/components/Icons';
import { Tab } from 'src/components/Tab';
import { Modal } from 'src/components/Modal';
import { InfoPanel } from 'src/components/InfoPanel';
import useAuthedRequest from 'src/hooks/useAuthedRequest';
import { Agent } from 'src/interfaces/agent.interface';

interface ApiKeyResponse {
  id: string;
  active: boolean;
  createdAt: string;
  environment: {
    id: string;
  };
}

type SavedApiKey = {
  id: string;
  createdAt: string;
  envId: string;
};

interface WidgetConfigProps {
  /**
   * Pre-selected agent. If provided, the agent selection dropdown will be disabled
   * and this agent will be used. Otherwise, a dropdown to select from available agents will be shown.
   */
  agent?: Agent;
}

export const WidgetConfig = ({ agent }: WidgetConfigProps) => {
  const { get, remove } = useAuthedRequest();
  const notification = useNotification();
  const [token, setToken] = useState<string>('');
  const [newApiKey, setNewApiKey] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [selectedAgent, setSelectedAgent] = useState<Option | null>(null);
  const [savedApiKeys, setSavedApiKeys] = useState<SavedApiKey[]>([]);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [keyToDelete, setKeyToDelete] = useState<string | null>(null);

  const expiryOptions = [
    { value: '1800', label: '30 minutes' },
    { value: '3600', label: '1 hour' },
    { value: '10800', label: '3 hours' },
    { value: '21600', label: '6 hours' },
    { value: 'never', label: 'Never expire' },
  ];
  const [expiryOption, setExpiryOption] = useState<Option>(expiryOptions[1]);

  const fetchApiKeys = useCallback(async () => {
    try {
      const response = (await get('/api-key')) as ApiKeyResponse[];
      const keys = response
        .filter((key: ApiKeyResponse) => key.active)
        .map((key: ApiKeyResponse) => ({
          id: key.id,
          createdAt: key.createdAt,
          envId: key.environment.id,
        }));
      setSavedApiKeys(keys);
    } catch (e) {
      console.error('Failed to load API keys', e);
      notification.error('Failed to load API keys');
    }
  }, [get, notification]);

  useEffect(() => {
    if (!agent) {
      const fetchAgents = async () => {
        try {
          setLoading(true);
          const response = await get('/agents');
          const agentList = response.items || [];
          setAgents(agentList);
          if (agentList.length > 0) {
            const defaultAgent = {
              value: agentList[0].id || '',
              label: agentList[0].name || `Agent ${agentList[0].id}`,
            };
            setSelectedAgent(defaultAgent);
          }
        } catch (error) {
          notification.error('Failed to load agents');
          console.error('Error loading agents:', error);
        } finally {
          setLoading(false);
        }
      };

      fetchAgents();
    } else if (agent.id) {
      // If agent is provided, set it as the selected agent
      setSelectedAgent({
        value: agent.id || '',
        label: agent.name || `Agent ${agent.id}`,
      });
    }

    fetchApiKeys();
  }, [agent, fetchApiKeys, get, notification]);

  const handleDeleteClick = (keyId: string) => {
    setKeyToDelete(keyId);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (keyToDelete) {
      try {
        await remove(`/api-key/${keyToDelete}`);
        await fetchApiKeys();
        notification.success('API key removed successfully');
      } catch (error) {
        console.error('Failed to remove API key:', error);
        notification.error('Failed to remove API key');
      }
      setKeyToDelete(null);
    }
    setDeleteConfirmOpen(false);
  };

  const cancelDelete = () => {
    setKeyToDelete(null);
    setDeleteConfirmOpen(false);
  };

  const agentOptions = agents.map((a) => ({
    value: a.id || '',
    label: a.name || `Agent ${a.id}`,
  }));

  const generateToken = async () => {
    setLoading(true);
    try {
      let url = '/web-widget/token';
      const params = new URLSearchParams();

      if (expiryOption.value !== 'never') {
        params.append('expiresIn', expiryOption.value);
      }

      // Use the pre-selected agent if provided, otherwise use the selected agent from dropdown
      const agentId = agent?.id || (selectedAgent?.value as string);
      params.append('agentId', agentId || '');

      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      const response = await get(url);
      setToken(response.token);
      notification.success('JWT generated successfully');
    } catch (error) {
      notification.error('Failed to generate JWT');
      console.error('Error generating JWT:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateApiKey = async () => {
    setLoading(true);
    try {
      const response = await get('/web-widget/signing-key');
      setNewApiKey(response.apiKey);
      await fetchApiKeys();
      notification.success('API key generated successfully');
    } catch (error) {
      notification.error('Failed to generate API key');
      console.error('Error generating API key:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    notification.success('Copied to clipboard');
  };

  const CopyableInput = ({
    value,
    ariaLabel,
  }: {
    value: string;
    ariaLabel: string;
  }) => (
    <div className="relative">
      <Input
        type="text"
        value={value}
        readOnly
        className="pr-10 font-mono text-sm"
      />
      <IconButton
        aria-label={ariaLabel}
        className="absolute inset-y-0 right-0 my-1 mr-1"
        onClick={() => copyToClipboard(value)}
      >
        <CopyIcon className="h-5 w-5 text-neutral-400 hover:text-neutral-600" />
      </IconButton>
    </div>
  );

  const CodeSnippet = ({
    code,
    ariaLabel = 'Copy code',
  }: {
    code: string;
    ariaLabel?: string;
  }) => (
    <div className="relative bg-neutral-700 text-neutral-0 p-3 rounded-md overflow-x-auto">
      <pre className="text-xs">{code}</pre>
      <IconButton
        aria-label={ariaLabel}
        className="absolute top-2 right-2 h-7 w-7 text-neutral-300 hover:bg-neutral-600 hover:text-neutral-0 focus:ring-neutral-300"
        onClick={() => copyToClipboard(code)}
      >
        <CopyIcon className="h-4 w-4" />
      </IconButton>
    </div>
  );

  const formatDate = (dateString: string) => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  const renderAgentSelection = () => {
    if (agent) {
      return (
        <div>
          <Label htmlFor="agent">Widget agent</Label>
          <Input
            type="text"
            value={agent.name || `Agent ${agent.id}`}
            disabled={true}
          />
          <p className="text-sm text-neutral-400 mt-1">
            The widget will connect to this agent.
          </p>
        </div>
      );
    }

    return (
      <div>
        <Label htmlFor="agent">Widget agent</Label>
        <SelectBox
          options={agentOptions}
          value={selectedAgent}
          onChange={(value) => {
            const option = agentOptions.find((opt) => opt.value === value);
            if (option) {
              setSelectedAgent(option);
            }
          }}
          disabled={loading || agentOptions.length === 0}
        />
        <p className="text-sm text-neutral-400 mt-1">
          Select the agent to be used with this widget (required)
        </p>
      </div>
    );
  };

  const currentAgentId =
    agent?.id || (selectedAgent?.value as string | undefined) || 'AGENT_ID';

  const widgetInitSnippet = `<div id="webcall-container"></div>
<script src="${process.env.REACT_APP_WIDGET_URL}/fluents-web-widget.iife.js"></script>
<script>
// Initialize the widget
window.FluentsWebWidget.init({
  targetId: 'webcall-container',
  jwtToken: '${token || 'JWT_FROM_CLICKING_GENERATE_JWT'}',
});
</script>`;

  const requestExample = `POST ${
    process.env.REACT_APP_BASE_API_URL
  }/web-widget/auth/widget-token
x-api-key: [your-api-key]
Content-Type: application/json

{
  "agentId": "${currentAgentId}",
  "expiresIn": 3600
}`;

  const responseExample = `{
  "token": "JWT_FROM_FLUENTS"
}`;

  const widgetInstallationExample = `<div id="webcall-container"></div>
<script src="${process.env.REACT_APP_WIDGET_URL}/fluents-web-widget.iife.js"></script>
<script>
  window.FluentsWebWidget.init({
    targetId: 'webcall-container',
    jwtToken: 'JWT_FROM_YOUR_BACKEND',
  });
</script>`;

  return (
    <>
      <Tab
        items={[
          {
            label: 'Generate JWT',
            content: (
              <div className="p-4">
                <p className="text-sm text-neutral-400 mb-4">
                  Generate a JWT to initialize the widget for a visitor. Use
                  this for testing and simple setups. For production, generate
                  JWTs from your backend using an API key (see API JWT
                  Generation).
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  {renderAgentSelection()}

                  <div>
                    <Label htmlFor="expiry">JWT lifetime</Label>
                    <SelectBox
                      options={expiryOptions}
                      value={expiryOption}
                      onChange={(value) => {
                        const option = expiryOptions.find(
                          (opt) => opt.value === value,
                        );
                        if (option) {
                          setExpiryOption(option);
                        }
                      }}
                      disabled={loading}
                    />
                    <p className="text-sm text-neutral-400 mt-1">
                      Choose how long the JWT is valid. Short-lived tokens are
                      recommended.
                    </p>
                    {expiryOption.value === 'never' && (
                      <InfoPanel
                        title="Security"
                        variant="warning"
                        className="mt-2 p-3"
                        description="Permanent tokens should be used with caution."
                      />
                    )}
                  </div>
                </div>

                <div className="flex items-start gap-2 mb-4">
                  <Button
                    onClick={generateToken}
                    disabled={loading || (!agent && !selectedAgent)}
                  >
                    Generate JWT
                  </Button>
                </div>

                {token && (
                  <InfoPanel
                    title="Generated JWT"
                    variant="warning"
                    className="mt-4 mb-4"
                    description={
                      <div className="space-y-2">
                        <CopyableInput value={token} ariaLabel="Copy JWT" />
                        <div className="text-sm">
                          Keep this JWT private. For production, generate
                          short-lived JWTs on your backend.
                        </div>
                      </div>
                    }
                  />
                )}

                <div className="bg-neutral-50 p-4 rounded-lg mt-6">
                  <div className="font-semibold mb-2">
                    Widget Installation Instructions
                  </div>
                  <div className="space-y-2 text-sm">
                    <div>
                      Paste this snippet{' '}
                      <span className="font-semibold">
                        right before{' '}
                        <code className="bg-neutral-100 px-1 rounded">
                          {'</body>'}
                        </code>
                      </span>{' '}
                      {!token && (
                        <>
                          and set{' '}
                          <code className="bg-neutral-100 px-1 rounded">
                            jwtToken
                          </code>{' '}
                          to the generated JWT.
                        </>
                      )}
                    </div>
                  </div>
                  <div className="mt-3">
                    <CodeSnippet
                      ariaLabel="Copy widget installation snippet"
                      code={widgetInitSnippet}
                    />
                  </div>
                </div>
              </div>
            ),
          },
          {
            label: 'API JWT Generation',
            content: (
              <div className="p-4">
                <p className="text-sm text-neutral-400 mb-4">
                  Create an API key for your backend to generate short-lived
                  JWTs for authenticated visitors. Use this option if you need
                  to dynamically authenticate users (recommended for
                  production).
                </p>
                <InfoPanel
                  title="Security"
                  variant="warning"
                  className="mb-4"
                  description={
                    <>
                      Store API keys on your backend (never in browser code).
                      Use it to generate short-lived JWTs and pass only the JWT
                      to the widget.
                    </>
                  }
                />

                <div className="flex items-start gap-2 mb-8">
                  <Button onClick={generateApiKey} disabled={loading}>
                    Generate API key
                  </Button>
                </div>

                {newApiKey && (
                  <InfoPanel
                    title="Your new API key"
                    variant="warning"
                    className="mt-4 mb-6 p-4"
                    description={
                      <div className="space-y-2">
                        <CopyableInput
                          value={newApiKey}
                          ariaLabel="Copy API key"
                        />
                        <p className="text-sm">
                          <span className="font-semibold">Important:</span> Save
                          this key somewhere safe. For security reasons,{' '}
                          <span className="font-semibold">
                            we cannot show it again.
                          </span>
                        </p>
                      </div>
                    }
                  />
                )}

                {savedApiKeys.length > 0 && (
                  <div className="mt-4">
                    <Label className="mb-2">Your API keys</Label>
                    <div className="border rounded-md overflow-hidden">
                      <table className="min-w-full divide-y divide-neutral-100">
                        <thead className="bg-neutral-50">
                          <tr>
                            <th
                              scope="col"
                              className="px-6 py-3 text-left text-xs font-medium text-neutral-400 uppercase tracking-wider"
                            >
                              Created
                            </th>
                            <th
                              scope="col"
                              className="px-6 py-3 text-left text-xs font-medium text-neutral-400 uppercase tracking-wider"
                            >
                              Key ID
                            </th>
                            <th
                              scope="col"
                              className="px-6 py-3 text-right text-xs font-medium text-neutral-400 uppercase tracking-wider"
                            >
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-neutral-0 divide-y divide-neutral-100">
                          {savedApiKeys.map((key) => (
                            <tr key={key.id}>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-400">
                                {formatDate(key.createdAt)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-mono">
                                {key.id}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                <button
                                  className="text-error hover:text-error-500"
                                  onClick={() => handleDeleteClick(key.id)}
                                >
                                  Remove
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                <div className="bg-neutral-50 p-4 rounded-lg mt-6">
                  <div className="font-semibold mb-2">How to use API keys</div>
                  <div className="text-sm leading-6">
                    <div className="space-y-4">
                      <p>
                        Use this API key on your backend to generate short-lived
                        JWTs for authenticated visitors. Do not expose the API
                        key in client-side code.
                      </p>

                      <ol className="list-decimal pl-5 space-y-4">
                        <li>
                          <span className="font-semibold">
                            Store the API key securely on your backend
                          </span>{' '}
                          (for example, in your secrets manager or environment
                          variables).
                        </li>

                        <li className="space-y-2">
                          <div>
                            <span className="font-semibold">
                              Create or modify an endpoint in your backend
                            </span>{' '}
                            that calls the Fluents token endpoint and returns
                            the JWT to your frontend.{' '}
                            <span className="font-semibold">
                              Include your API key in the{' '}
                              <code className="bg-neutral-100 px-1 rounded">
                                x-api-key
                              </code>{' '}
                              header.
                            </span>
                          </div>

                          <div className="text-xs uppercase tracking-wider text-neutral-500">
                            From your backend:
                          </div>

                          <div className="space-y-2">
                            <div className="font-semibold">Request</div>
                            <CodeSnippet
                              ariaLabel="Copy request example"
                              code={requestExample}
                            />
                          </div>

                          <div className="space-y-1">
                            <div className="font-semibold">
                              Request body parameters
                            </div>
                            <ul className="list-disc pl-5 space-y-1">
                              <li>
                                <code className="bg-neutral-100 px-1 rounded">
                                  agentId
                                </code>{' '}
                                — The agent the widget should connect to.
                              </li>
                              <li>
                                <code className="bg-neutral-100 px-1 rounded">
                                  expiresIn
                                </code>{' '}
                                — Token lifetime in seconds. Allowed values:{' '}
                                <code className="bg-neutral-100 px-1 rounded">
                                  1800
                                </code>
                                ,{' '}
                                <code className="bg-neutral-100 px-1 rounded">
                                  3600
                                </code>
                                ,{' '}
                                <code className="bg-neutral-100 px-1 rounded">
                                  10800
                                </code>
                                ,{' '}
                                <code className="bg-neutral-100 px-1 rounded">
                                  21600
                                </code>
                                .
                              </li>
                            </ul>
                          </div>

                          <div className="space-y-1">
                            <div className="font-semibold">
                              Required headers
                            </div>
                            <ul className="list-disc pl-5 space-y-1">
                              <li>
                                <code className="bg-neutral-100 px-1 rounded">
                                  x-api-key
                                </code>{' '}
                                — Your API key.
                              </li>
                            </ul>
                          </div>
                          <div>
                            <span className="font-semibold">Response</span>
                          </div>
                          <CodeSnippet
                            ariaLabel="Copy response example"
                            code={responseExample}
                          />
                        </li>

                        <li className="space-y-2">
                          <div>
                            <span className="font-semibold">
                              When a signed-in user needs to access the widget,
                              call your backend endpoint to generate a JWT for
                              that user
                            </span>{' '}
                            (send only the JWT to the browser)
                          </div>
                        </li>

                        <li>
                          <span className="font-semibold">
                            Initialize the widget with the JWT from your backend
                            endpoint
                          </span>
                        </li>
                      </ol>

                      <hr className="border-neutral-200" />

                      <div className="space-y-3">
                        <div className="font-semibold text-base">
                          Widget Installation Instructions
                        </div>
                        <div>
                          Paste this snippet{' '}
                          <span className="font-semibold">
                            right before{' '}
                            <code className="bg-neutral-100 px-1 rounded">
                              {'</body>'}
                            </code>
                          </span>{' '}
                          on the page where you want the widget to appear:
                        </div>

                        <CodeSnippet
                          ariaLabel="Copy widget installation example"
                          code={widgetInstallationExample}
                        />

                        <div className="space-y-1">
                          <div className="font-semibold">Notes</div>
                          <ul className="list-disc pl-5 space-y-1">
                            <li>
                              Generate a{' '}
                              <span className="font-semibold">
                                new JWT per visitor session
                              </span>{' '}
                              (recommended) and keep it{' '}
                              <span className="font-semibold">short-lived</span>
                              .
                            </li>
                            <li>
                              If the JWT expires, generate a new one on your
                              backend and re-initialize the widget.
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ),
          },
        ]}
      />

      <Modal
        title="Remove API key"
        isOpen={deleteConfirmOpen}
        onClose={cancelDelete}
        actionButton={
          <Button color="error" onClick={confirmDelete}>
            Remove
          </Button>
        }
      >
        <p>
          Are you sure you want to remove this API key? This action cannot be
          undone.
        </p>
        <p className="mt-2 text-sm text-neutral-400">
          Any applications using this key will no longer be able to generate
          JWTs.
        </p>
      </Modal>
    </>
  );
};
